import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get, pick, isEmpty } from 'lodash';
import { compose, withProps } from 'recompose';
import addYears from 'date-fns/add_years';
import { PAGE_TYPES } from '@sparefoot/tracking-events';

import { buildSearchLink } from 'lib/search';
import { oonFacilityPageSelector } from 'store/selectors/facilities';
import {
	withPageViewTracking
} from 'components/core/library/tracking-utils';
import { withSearchHandler } from 'decorators';
import { withHoursAndDirectionsHandlers } from 'decorators/facility';

import { ClickToShowCallButton } from 'components/contact/ClickToShowCallButton';
import { PhoneCalloutLarge } from 'components/contact/PhoneCallout';
import { BackToTop } from 'components/core/BackToTop';
import { Button } from 'components/core/Button';
import { Card } from 'components/core/Card';
import { Link } from 'components/core/Link';
import { Image } from 'components/core/Image';
import { StickyContainer } from 'components/core/StickyContainer';
import {
	Page,
	Container,
	Section,
	Group,
	Row,
	Column
} from 'components/core/Page';
import { FacilityBreadcrumbs } from 'components/facility/FacilityBreadcrumbs';
import { HoursAndDirectionsModal } from 'components/facility/HoursAndDirectionsModal';
import { FacilityImageNotAvailable } from 'components/facility/ImageNotAvailable';
import { H1, H2, H3, Paragraph } from 'components/core/Typography';
import { PODSPromo } from 'components/partner/PODSPromo';
import { FacilityList } from 'components/search/FacilityList';
import { NearbyUnits } from 'components/search/NearbyUnits';
import { SearchBox } from 'components/search/SearchBox';
import { ClickToShowNumberButton } from 'components/contact/ClickToShowNumberButton';

import { Clock, CheckmarkShield, Search } from 'components/svg';
import callCenterAgent from 'assets/images/confirmation/call-center-agent.jpg';

import './oonFacility.scss';

export const enhance = compose(
	connect(oonFacilityPageSelector),
	withHoursAndDirectionsHandlers,
	withPageViewTracking(PAGE_TYPES.OON_FACILITY),
	withProps((props) => ({
		phone: pick(props.phone, [
			'nationalSparephone',
			'nationalSparephoneUnformatted'
		]),
		localSparephone: get(props, 'phone.localSparephone'),
		localSparephoneUnformatted: get(
			props,
			'phone.localSparephoneUnformatted'
		),
		claimURL: `https://marketing.sparefoot.com/?sfdcoon=${get(
			props,
			'facility.id'
		)}`,
		facility: props.facility
	})),
	withSearchHandler
);

export function OONFacilityPage({
	actions,
	facility,
	phone,
	nearbyFacilities,
	selectedFacility,
	setSelectedFacility,
	handleMapAndHoursClose,
	handleSearch,
	claimURL,
	reservationWindow,
	searchId
}) {
	return (
		<Page className="oon-facility-page" pageType={PAGE_TYPES.OON_FACILITY}>
			<StickyContainer>
					<div className="sticky-search">
						<Paragraph eyebrow color="black">
							Search for storage units
						</Paragraph>
						<SearchBox
							darkButton
							handleSearch={handleSearch}
						/>
					</div>
			</StickyContainer>
			<Container narrow>
				<FacilityBreadcrumbs
					facility={facility}
				/>
				{get(facility, 'nearby.totalFacilities') !== 0 && (
						<Button
							className="nearby-search-button"
							rel="nofollow"
							href={buildSearchLink({
								location: facility.location.zip
							})}
						>
							See {get(facility, 'nearby.totalFacilities', '')}{' '}
							more facilities{' '}
							<span className="hide-for-tablet">
								near this facility
							</span>
						</Button>
				)}
			</Container>
			<Section >
				<Container narrow gutters="large">
					<Row>
						<Column large={6}>
							<FacilityImageNotAvailable isOON>
								<Paragraph size="small" color="white">
									No image available
								</Paragraph>
								<Paragraph
									size="small"
									color="white"
									className="claim-link"
								>
									Facility owner: <br />
									<Link
										href={claimURL}
										color="white"
									>
										Join the SpareFoot network
									</Link>
								</Paragraph>
							</FacilityImageNotAvailable>
						</Column>
						<Column large={6}>
							<div
								className="facility-header"
								itemScope
								itemType="https://schema.org/SelfStorage"
							>
								<Group>
									<Paragraph eyebrow>
										NOT IN THE SPAREFOOT STORAGE NETWORK
									</Paragraph>
									<H1 size="large">
										<span itemProp="name">
											{facility.name}
										</span>
									</H1>
									<H2>{facility.location.text}</H2>
								</Group>
								<Group>
									<ClickToShowNumberButton
										className="phone-number-button"
										callToActionText="Call SpareFoot"
										size="standard"
										fetchPhoneNumber={
											actions.getTollfreePhoneNumber
										}
									/>
								</Group>
								{!!facility.phone && (
									<div className="facility-phone">
										<ClickToShowCallButton
											initialLabel="Call this facility"
											hideIcon
											link
											phoneNumber={facility.phone}
											unformattedPhoneNumber={facility.phone.replace(
												/\D/g,
												''
											)}
										/>
										<ClickToShowCallButton
											initialLabel="Pay my bill"
											hideIcon
											link
											phoneNumber={facility.phone}
											unformattedPhoneNumber={facility.phone.replace(
												/\D/g,
												''
											)}
										/>
									</div>
								)}
							</div>
						</Column>
					</Row>
				</Container>
			</Section>
			<Section>
				<Container narrow>
					<Card className="about-sparefoot" hideBorder>
						<H3 size="medium" className="headline">
							About SpareFoot
						</H3>
						<Row className="items">
							<Column large={4}>
								<div className="item">
									<Search />
									<Paragraph color="black" eyebrow last>
										Browse the most storage options
										nationwide
									</Paragraph>
								</div>
							</Column>
							<Column large={4}>
								<div className="item">
									<CheckmarkShield />
									<Paragraph color="black" eyebrow last>
										Pick the best solution for your needs
									</Paragraph>
								</div>
							</Column>
							<Column large={4}>
								<div className="item">
									<Clock />
									<Paragraph color="black" eyebrow last>
										Reserve for free in minutes.
									</Paragraph>
								</div>
							</Column>
						</Row>
					</Card>
				</Container>
			</Section>
			<Section className="pricing-stats">
				{!isEmpty(
					get(facility, 'nearby.pricingStats.lowestPrices')
				) && (
					<Container narrow>
						<H3 size="medium">
							Units available at nearby facilities
						</H3>
						<NearbyUnits
							nearbyZip={facility.location.zip}
							nearbyPrices={get(
								facility,
								'nearby.pricingStats.lowestPrices'
							)}
						/>
					</Container>
				)}
			</Section>
			<div className="nearby-facilities">
				{!isEmpty(nearbyFacilities) && (
					<Section>
						<Container narrow>
							<H3 size="medium">Recommended facilities nearby</H3>
							{!!nearbyFacilities.length && (
								<FacilityList
									facilities={nearbyFacilities}
									openModal={setSelectedFacility}
									phone={phone}
									getLocalPhoneNumberByZipCode={
										actions.getLocalPhoneNumberByZipCode
									}
									searchId={searchId}
								/>
							)}
						</Container>
					</Section>
				)}
				<Section >
					<Container narrow>
						<div className="facility-search">
							<H3 size="medium">Find self-storage near you</H3>
							<SearchBox
								handleSearch={handleSearch}
								reservationWindow={reservationWindow}
							/>
						</div>
					</Container>
				</Section>
				<Section>
					<Container narrow>
						<PhoneCalloutLarge
							headline="Want to save time and money?"
							subheading="Call SpareFoot and let our experts find you the best deal on storage."
							image={callCenterAgent}
							formattedPhone={phone.nationalSparephone}
							unformattedPhone={
								phone.nationalSparephoneUnformatted
							}
							fetchPhoneNumber={actions.getTollfreePhoneNumber}
						/>
					</Container>
				</Section>
			</div>
			<Section>
				<Container narrow>
					<div>
						<div className="facility-map">
							<H3 size="medium">{facility.name} location</H3>
							<Image
								className="static-map"
								src={facility.staticMapUrlLarge}
							/>
						</div>
					</div>
				</Container>
			</Section>
			<Section>
				<Container narrow>
					<PODSPromo />
				</Container>
			</Section>
			<Section >
				<Container narrow>
					<Card className="claim-facility" hideBorder>
						<div className="claim-content">
							<H3 size="small">Do you own this facility?</H3>
							<Paragraph last>
								Claim today and join the SpareFoot Network!
							</Paragraph>
						</div>
						<Button
							href={claimURL}
						>
							Claim this facility
						</Button>
					</Card>
				</Container>
			</Section>
			<HoursAndDirectionsModal
				showing={!!selectedFacility}
				onClose={handleMapAndHoursClose}
				onSubmit={actions.getFacilityHoursAndDirection}
				facilityId={selectedFacility}
			/>
			<BackToTop />
		</Page>
	);
}

OONFacilityPage.propTypes = {
	actions: PropTypes.shape({
		getFacilityHoursAndDirection: PropTypes.func,
		getTollfreePhoneNumber: PropTypes.func,
		getLocalPhoneNumberByZipCode: PropTypes.func
	}),
	nearbyFacilities: PropTypes.arrayOf(PropTypes.object),
	phone: PropTypes.object,
	facility: PropTypes.object.isRequired,
	selectedFacility: PropTypes.number,
	setSelectedFacility: PropTypes.func,
	handleMapAndHoursClose: PropTypes.func,
	handleSearch: PropTypes.func.isRequired,
	claimURL: PropTypes.string.isRequired,
	reservationWindow: PropTypes.instanceOf(Date),
	searchId: PropTypes.string
};

OONFacilityPage.defaultProps = {
	reservationWindow: addYears(new Date(), 1)
};

export default enhance(OONFacilityPage);
